import { useNavigate } from "react-router-dom";
import { Sparkles, AlertCircle, ArrowRight, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useBillingStatus } from "../lib/billing";

const DISMISS_KEY = "pragma_trial_banner_dismissed_until";

export default function TrialBanner() {
  const navigate = useNavigate();
  const { billing } = useBillingStatus();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (until && Date.now() < until) setDismissed(true);
    } catch { /* ignore */ }
  }, []);

  if (!billing) return null;
  // Show banner in trial (low days) OR when payment is overdue.
  const inTrial = billing.in_trial && !billing.has_subscription;
  const isPastDue = billing.subscription_status === "past_due";
  const needsPayment = billing.needs_payment;
  if (!inTrial && !isPastDue && !needsPayment) return null;
  // Hide in-trial banner once user dismissed (only when >3 days left)
  if (inTrial && dismissed && billing.trial_days_left > 3) return null;

  const dismissFor24h = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000)); } catch { /* ignore */ }
    setDismissed(true);
  };

  let icon = <Sparkles />;
  let title = "";
  let cta = "Manage billing";
  let tone = "trial";

  if (inTrial) {
    title = `${billing.trial_days_left} day${billing.trial_days_left === 1 ? "" : "s"} left in your free trial — add a payment method to keep your workspace running.`;
    cta = "Add payment method";
    tone = billing.trial_days_left <= 3 ? "urgent" : "trial";
    icon = billing.trial_days_left <= 3 ? <AlertCircle /> : <Sparkles />;
  } else if (isPastDue) {
    title = "Your last payment failed. Update your card to avoid losing access.";
    cta = "Update payment";
    tone = "urgent";
    icon = <AlertCircle />;
  } else if (needsPayment) {
    title = "Your free trial has ended. Add a payment method to keep using Pragma.";
    cta = "Start subscription";
    tone = "urgent";
    icon = <AlertCircle />;
  }

  return (
    <div className={`trial-banner trial-banner-${tone}`} data-testid="trial-banner">
      <div className="trial-banner-icon">{icon}</div>
      <div className="trial-banner-text">{title}</div>
      <button
        type="button"
        className="trial-banner-cta"
        onClick={() => navigate("/billing")}
        data-testid="trial-banner-cta"
      >
        {cta} <ArrowRight strokeWidth={2.5} />
      </button>
      {inTrial && billing.trial_days_left > 3 && (
        <button
          type="button"
          className="trial-banner-dismiss"
          onClick={dismissFor24h}
          aria-label="Dismiss for 24 hours"
          data-testid="trial-banner-dismiss"
        >
          <X />
        </button>
      )}
    </div>
  );
}
